import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { todayISO } from "@/lib/utils";
import { handleApiError } from "@/services/api";
import { flockService } from "@/services/masterDataService";
import {
  flockMetricsService,
  METRIC_FIELDS,
  toMeasure,
  isEmptySubmission,
  type FlockDailyMetrics,
  type MetricKey,
} from "@/services/flockMetricsService";
import type { Flock } from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type Draft = Record<MetricKey, string>;

const emptyDraft = (): Draft =>
  METRIC_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {} as Draft);

/**
 * Daily flock measurements: body weight, egg weight, water, light, shed
 * conditions.
 *
 * The form loads whatever is already recorded for the chosen flock and day, so
 * a second visit CORRECTS rather than overwrites blind — the write is an upsert
 * and a blank box means "no reading", so opening a day with readings and saving
 * an empty form would erase them. The server refuses that; this screen says so
 * before the round trip.
 */
export default function FlockMetricsPage() {
  const isViewer = useIsViewer();

  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [flockId, setFlockId] = useState<number | "">("");
  const [date, setDate] = useState(todayISO());
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [notes, setNotes] = useState("");
  const [existing, setExisting] = useState<FlockDailyMetrics | null>(null);

  const [rows, setRows] = useState<FlockDailyMetrics[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // The day could not be read. Saving then would assign every column from a
  // blank form and erase the readings that are stored, so Save is locked.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    flockService
      .list()
      .then((f) => {
        setFlocks(f);
        if (f.length > 0) setFlockId(f[0].id);
      })
      .catch((err) => setError(handleApiError(err)));
  }, []);

  const loadRows = useCallback(async () => {
    if (flockId === "") return;
    try {
      setRows(await flockMetricsService.list({ flock_id: Number(flockId) }));
    } catch (err) {
      setError(handleApiError(err));
    }
  }, [flockId]);

  // Prefill from whatever is already recorded for this flock-day. A 404 is the
  // normal answer for a day nobody measured, not an error to show.
  const loadDay = useCallback(async () => {
    if (flockId === "") return;
    setNotice("");
    setError("");
    setLoadFailed(false);
    try {
      const m = await flockMetricsService.forFlockDate(Number(flockId), date);
      setExisting(m);
      setDraft(
        METRIC_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f.key]: m[f.key] == null ? "" : String(m[f.key]) }),
          {} as Draft,
        ),
      );
      setNotes(m.notes ?? "");
    } catch (err) {
      setExisting(null);
      setDraft(emptyDraft());
      setNotes("");
      // Only a 404 means "nobody measured this day". Anything else (a 500, a
      // dropped connection) means we don't know what is stored.
      if (!(axios.isAxiosError(err) && err.response?.status === 404)) {
        setLoadFailed(true);
        setError(handleApiError(err));
      }
    }
  }, [flockId, date]);

  useEffect(() => {
    void loadDay();
    void loadRows();
  }, [loadDay, loadRows]);

  const payload = () => ({
    flock_id: Number(flockId),
    date,
    ...METRIC_FIELDS.reduce(
      (acc, f) => ({ ...acc, [f.key]: toMeasure(draft[f.key]) }),
      {} as Record<MetricKey, number | null>,
    ),
    notes: notes.trim() === "" ? null : notes.trim(),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");

    const body = payload();
    if (isEmptySubmission(body)) {
      // Said here rather than after a round trip, because the consequence is
      // destructive: the write assigns every column, so an empty save would
      // clear a day that already had readings.
      setError(
        existing
          ? "Nothing entered. Saving an empty form would erase the readings already recorded for this day."
          : "Enter at least one measurement.",
      );
      return;
    }

    setSaving(true);
    // Captured BEFORE the reload: loadDay() replaces `existing`, and it also
    // clears the notice — so setting the message first meant the reload wiped
    // it and the user got no confirmation at all. Caught by the form E2E.
    const wasExisting = existing !== null;
    try {
      await flockMetricsService.save(body);
      await loadDay();
      await loadRows();
      setNotice(wasExisting ? "Corrected." : "Recorded.");
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Flock measurements | Poultry Book"
        description="Daily body weight, egg weight, water, light and shed conditions."
      />
      <PageBreadcrumb pageTitle="Flock measurements" />

      <div className="space-y-6">
        <form
          onSubmit={handleSubmit}
          className="card p-5 sm:p-6"
        >
          <p className="mb-5 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
            Record whatever was measured today. Every box is optional — a farm that weighs a
            sample on Mondays and nothing else fills in one field and leaves the rest blank.
            A blank box means <span className="font-medium">no reading</span>, not zero.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label htmlFor="flock">Flock</Label>
              <select
                id="flock"
                className={selectClass}
                value={flockId}
                onChange={(e) => setFlockId(e.target.value === "" ? "" : Number(e.target.value))}
                disabled={isViewer}
              >
                {flocks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.batch_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                disabled={isViewer}
              />
            </div>
          </div>

          {existing && (
            <p
              role="status"
              className="mt-4 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
            >
              This day already has readings. Saving replaces them — and a box you clear is
              recorded as no reading.
            </p>
          )}

          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {METRIC_FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={f.key}>
                  {f.label} <span className="text-gray-400">({f.unit})</span>
                </Label>
                <Input
                  id={f.key}
                  type="number"
                  step={f.step}
                  value={draft[f.key]}
                  placeholder="—"
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  disabled={isViewer}
                />
                {f.hint && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{f.hint}</p>
                )}
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-3">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isViewer}
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-400"
            >
              {error}
            </div>
          )}
          {notice && (
            <div
              role="status"
              className="mt-4 rounded-lg bg-success-50 px-3 py-2 text-sm text-success-700 dark:bg-success-500/15 dark:text-success-400"
            >
              {notice}
            </div>
          )}

          {!isViewer && (
            <div className="mt-5">
              <Button type="submit" size="sm" disabled={saving || flockId === "" || loadFailed}>
                {saving ? "Saving…" : existing ? "Update this day" : "Record"}
              </Button>
            </div>
          )}
        </form>

        <div className="overflow-hidden card">
          <div className="px-5 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Recorded days
            </h3>
          </div>
          {rows.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-gray-500 dark:text-gray-400">
              Nothing recorded for this flock yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader className="px-5 py-3 text-left text-xs text-gray-500">
                      Date
                    </TableCell>
                    {METRIC_FIELDS.map((f) => (
                      <TableCell
                        key={f.key}
                        isHeader
                        className="px-4 py-3 text-right text-xs text-gray-500"
                      >
                        {f.label.replace("Shed temperature, ", "Temp ")} ({f.unit})
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                      <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                        {r.date.slice(0, 10)}
                      </TableCell>
                      {METRIC_FIELDS.map((f) => (
                        <TableCell
                          key={f.key}
                          className="px-4 py-3 text-right text-sm tabular-nums text-gray-800 dark:text-white/90"
                        >
                          {/* An em dash, not a 0. The distinction between "not
                              measured" and "measured as zero" survives all the
                              way to the screen or it was pointless to keep. */}
                          {r[f.key] == null ? (
                            <span className="text-gray-300 dark:text-gray-600">—</span>
                          ) : (
                            r[f.key]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
